import { FC, useEffect } from 'react';

import { useSession } from '@eneco/auth';
import {
  ValidationProblemDetails,
  getEnecoBeXapiSiteApiV1Profile,
  postEnecoBeXapiSiteApiV1ProfileItsme,
} from '@eneco/dc-be/client';
import { useRouter } from '@eneco/next';
import { ProfileManagementLoginDetailsLinkItsmeRendering } from '@sitecore/types/ProfileManagementLoginDetailsLinkItsme';
import { Session } from 'next-auth';
import { signIn } from 'next-auth/react';
import { useSWRConfig } from 'swr';

import {
  ERROR_ITSME_LINKED_TO_OTHER_ACCOUNT,
  NOTIFICATION_ITSME_LINK,
  QUERY_PARAMETER_ERROR,
  QUERY_PARAMETER_NOTIFICATION,
  QUERY_PARAMETER_SUCCESS,
} from '../ProfileManagementLoginDetails';
import LoadingPage from '../../../../../components/LoadingSpinnerWithText';

export const ITSME_LINK__OKTA_ACCESS_TOKEN_KEY = 'itsme-link.token';

const checkIfItsmeSession = (data: Session): boolean => {
  const jwtParts = data?.idToken?.split('.') || [];

  if (jwtParts.length !== 3) {
    return false;
  }

  let jwtContent: { iss: string[] } | null = null;
  try {
    jwtContent = JSON.parse(atob(jwtParts[1]));
  } catch {
    return false;
  }

  return !!(jwtContent && jwtContent.iss.includes('itsme'));
};

const getItsmeId = (data: Session): string | undefined => {
  const jwtParts = data?.idToken?.split('.') || [];

  if (jwtParts.length !== 3) {
    return undefined;
  }

  let jwtContent: { sub: string[] } | null = null;
  try {
    jwtContent = JSON.parse(atob(jwtParts[1]));
  } catch {
    return undefined;
  }

  return jwtContent?.sub.toString();
};

const ProfileManagementLoginDetailsLinkItsme: FC<ProfileManagementLoginDetailsLinkItsmeRendering> = ({ fields }) => {
  const { data } = useSession();
  const router = useRouter();
  const { mutate } = useSWRConfig();

  useEffect(() => {
    if (data && data.idToken) {
      const isItsmeSession = checkIfItsmeSession(data);
      if (!isItsmeSession) {
        getEnecoBeXapiSiteApiV1Profile()
          .catch(error => {
            router.push(
              `${fields.loginManagementConfirmItsmeSuccessRedirectLink.value.href}?${QUERY_PARAMETER_SUCCESS}=false&${QUERY_PARAMETER_NOTIFICATION}=${NOTIFICATION_ITSME_LINK}`,
            );
          })
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .then(({ response, data }: any) => {
            let newLocationPushed = false;
            if (response.ok) {
              if (data.data?.hasItsmeLogin) {
                newLocationPushed = true;
                mutate('session');
                router.push(
                  `${fields.loginManagementConfirmItsmeSuccessRedirectLink.value.href}?${QUERY_PARAMETER_SUCCESS}=true&${QUERY_PARAMETER_NOTIFICATION}=${NOTIFICATION_ITSME_LINK}`,
                );
              }
            }
            if (!newLocationPushed) {
              const queryParams = new URLSearchParams(window.location.search);

              let url = `${fields.loginManagementConfirmItsmeSuccessRedirectLink.value.href}?${QUERY_PARAMETER_SUCCESS}=false&${QUERY_PARAMETER_NOTIFICATION}=${NOTIFICATION_ITSME_LINK}`;

              if (queryParams.get('error') === 'OtherUserAlreadyLinked') {
                url += `&${QUERY_PARAMETER_ERROR}=${ERROR_ITSME_LINKED_TO_OTHER_ACCOUNT}`;
              }

              router.push(url);
            }
          });
      } else {
        const oktaAccessToken = sessionStorage.getItem(ITSME_LINK__OKTA_ACCESS_TOKEN_KEY);
        sessionStorage.removeItem(ITSME_LINK__OKTA_ACCESS_TOKEN_KEY);
        const redirectUrl = fields.loginManagementConfirmItsmeOktaRedirectLink.value.href;
        if (!oktaAccessToken) {
          signIn('okta', { callbackUrl: redirectUrl });
        } else {
          const itsmeId = getItsmeId(data);
          postEnecoBeXapiSiteApiV1ProfileItsme({
            headers: {
              Authorization: `Bearer ${oktaAccessToken}`,
            },
            body: {
              itsmeId: itsmeId!,
            },
          })
            .then(result => {
              const { response, error } = result;
              if (response.ok) {
                return signIn('okta', { callbackUrl: redirectUrl });
              }

              const unexpectedErrors =
                (error?.errors?.errors as ValidationProblemDetails['errors'])?.['Unexpected'] || [];

              if (unexpectedErrors.includes('ItsmeLink.OtherUserAlreadyLinked')) {
                const url = redirectUrl + '?error=OtherUserAlreadyLinked';
                return signIn('okta', { callbackUrl: url.toString() });
              }

              signIn('okta', { callbackUrl: redirectUrl });
            })
            .catch(err => {
              signIn('okta', { callbackUrl: redirectUrl });
            });
        }
      }
    }
  }, [
    data,
    data?.providerAccountId,
    fields.loginManagementConfirmItsmeOktaRedirectLink.value.href,
    fields.loginManagementConfirmItsmeSuccessRedirectLink.value.href,
    mutate,
    router,
  ]);

  return <LoadingPage text={fields.loginManagementConfirmItsmeLink.value.text} />;
};

export default ProfileManagementLoginDetailsLinkItsme;
